import React, { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { animations } from '../../design-system/tokens/animations';
import { shadows } from '../../design-system/tokens/shadows';
import {
  buildDateFromParts,
  getDaysInMonthForParts,
  getYearOptions,
  isDayCompatibleWithMonth,
  parseDateParts,
  type DateParts,
} from '../../utils/dateUtils';

const Wrapper = styled.fieldset`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
`;

const Legend = styled.legend`
  padding: 0;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  font-family: ${typography.fontFamily.primary};
`;

const HelperText = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme.colors.textSecondary};
  font-family: ${typography.fontFamily.primary};
`;

const DateRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[3]};
  width: 100%;
  min-width: 0;
`;

const PartGroup = styled.div<{ $minWidth: string; $flex?: string }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  min-width: ${props => props.$minWidth};
  flex: ${props => props.$flex || '1 1 auto'};
`;

const SubLabel = styled.label`
  display: block;
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme.colors.textSecondary};
  font-family: ${typography.fontFamily.primary};
`;

const Select = styled.select<{ $hasError?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: ${spacing[3]} ${spacing[8]} ${spacing[3]} ${spacing[3]};
  border: ${borders.width.base} solid
    ${props => (props.$hasError ? props.theme.colors.error : props.theme.colors.border)};
  border-radius: ${borders.radius.lg};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  line-height: ${typography.lineHeight.normal};
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23666' d='M1.41 0L6 4.58 10.59 0 12 1.41l-6 6-6-6z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right ${spacing[3]} center;
  transition: ${animations.transition(
    ['border-color', 'box-shadow', 'background-color'],
    animations.duration.normal,
    animations.easing.easeOut
  )};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => shadows.colored.sm(props.theme.colors.primary)};
  }

  &:disabled {
    background-color: ${props => props.theme.colors.surfaceHover};
    color: ${props => props.theme.colors.textSecondary};
    cursor: not-allowed;
  }
`;

const ErrorText = styled.span`
  color: ${props => props.theme.colors.error};
  font-size: ${typography.fontSize.xs};
  font-family: ${typography.fontFamily.primary};
`;

export interface DateOfBirthFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Controlled day/month/year so incomplete selections survive step navigation. */
  parts?: DateParts;
  onPartsChange?: (parts: DateParts) => void;
  error?: string;
  id?: string;
  disabled?: boolean;
}

const DateOfBirthField: React.FC<DateOfBirthFieldProps> = ({
  value,
  onChange,
  parts: controlledParts,
  onPartsChange,
  error,
  id,
  disabled = false,
}) => {
  const { t, i18n } = useTranslation();
  const reactId = useId();
  const baseId = id || `dob-${reactId}`;
  const helperId = `${baseId}-helper`;
  const errorId = `${baseId}-error`;
  const dayId = `${baseId}-day`;
  const monthId = `${baseId}-month`;
  const yearId = `${baseId}-year`;

  const isControlled = controlledParts !== undefined;
  const [uncontrolledParts, setUncontrolledParts] = useState<DateParts>(() =>
    parseDateParts(value)
  );
  const [compatibilityError, setCompatibilityError] = useState<string | undefined>();

  const parts = isControlled ? controlledParts! : uncontrolledParts;

  const setParts = (next: DateParts | ((prev: DateParts) => DateParts)) => {
    const resolved = typeof next === 'function' ? next(parts) : next;
    if (!isControlled) {
      setUncontrolledParts(resolved);
    }
    onPartsChange?.(resolved);
  };

  const isGreek = (i18n.language || 'en').startsWith('gr') || (i18n.language || 'en').startsWith('el');

  // Uncontrolled: keep local day/month/year when parent value is incomplete ('').
  useEffect(() => {
    if (isControlled) return;
    setUncontrolledParts(prev => {
      const built = buildDateFromParts(prev.day, prev.month, prev.year);
      if (value === built) return prev;
      if (!value && !built) return prev;
      return parseDateParts(value);
    });
  }, [value, isControlled]);

  // Controlled: if parent only has ISO value and empty parts, hydrate once from value.
  useEffect(() => {
    if (!isControlled || !onPartsChange) return;
    const hasParts = !!(controlledParts!.day || controlledParts!.month || controlledParts!.year);
    if (!hasParts && value) {
      onPartsChange(parseDateParts(value));
    }
  }, [isControlled, value, controlledParts, onPartsChange]);

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: String(i + 1).padStart(2, '0'),
        label: new Date(2000, i, 1).toLocaleDateString(isGreek ? 'el-GR' : 'en-GB', {
          month: 'long',
        }),
      })),
    [isGreek]
  );

  const daysInMonth = getDaysInMonthForParts(parts.month, parts.year);
  const dayOptions = useMemo(() => {
    // Keep a previously selected day visible even when it is invalid for the month,
    // so the user can see what needs correcting (never silently drop the selection).
    const base = Array.from({ length: daysInMonth }, (_, i) => String(i + 1).padStart(2, '0'));
    if (parts.day && !base.includes(parts.day)) {
      return [...base, parts.day].sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
    }
    return base;
  }, [daysInMonth, parts.day]);
  const yearOptions = useMemo(() => getYearOptions(), []);

  const dayValue = parts.day && dayOptions.includes(parts.day) ? parts.day : parts.day || '';
  const monthValue = monthOptions.some(m => m.value === parts.month) ? parts.month : '';
  const yearValue = yearOptions.includes(parseInt(parts.year, 10)) ? parts.year : '';

  const incompatibleDay =
    !!parts.day &&
    !!parts.month &&
    !isDayCompatibleWithMonth(parts.day, parts.month, parts.year);

  const displayError =
    error ||
    compatibilityError ||
    (incompatibleDay ? t('register.dateDayInvalidForMonth') : undefined);
  const describedBy = [displayError ? errorId : null, helperId].filter(Boolean).join(' ') || undefined;

  const updatePart = (field: 'day' | 'month' | 'year', partValue: string) => {
    const next: DateParts = { ...parts, [field]: partValue };

    // Preserve day/month/year selections; explain invalid combinations instead of clearing.
    if (
      next.day &&
      next.month &&
      !isDayCompatibleWithMonth(next.day, next.month, next.year)
    ) {
      setCompatibilityError(t('register.dateDayInvalidForMonth'));
    } else {
      setCompatibilityError(undefined);
    }

    setParts(next);
    onChange(buildDateFromParts(next.day, next.month, next.year));
  };

  return (
    <Wrapper aria-invalid={!!displayError || undefined} disabled={disabled || undefined}>
      <Legend id={`${baseId}-legend`}>{t('register.dateOfBirthShort')}</Legend>
      <HelperText id={helperId}>{t('register.dateOfBirthHelper')}</HelperText>
      <DateRow role="group" aria-labelledby={`${baseId}-legend`}>
        <PartGroup $minWidth="5.5rem" $flex="0 1 5.5rem">
          <SubLabel htmlFor={dayId}>{t('register.dateDay')}</SubLabel>
          <Select
            id={dayId}
            name={`${baseId}-day`}
            value={dayValue}
            disabled={disabled}
            $hasError={!!displayError}
            onChange={e => updatePart('day', e.target.value)}
            aria-label={t('register.dateDay')}
            aria-describedby={describedBy}
            aria-invalid={!!displayError || undefined}
            autoComplete="bday-day"
          >
            <option value="">{t('register.dateSelectDay')}</option>
            {dayOptions.map(day => (
              <option key={day} value={day}>
                {parseInt(day, 10)}
              </option>
            ))}
          </Select>
        </PartGroup>

        <PartGroup $minWidth="11rem" $flex="1 1 11rem">
          <SubLabel htmlFor={monthId}>{t('register.dateMonth')}</SubLabel>
          <Select
            id={monthId}
            name={`${baseId}-month`}
            value={monthValue}
            disabled={disabled}
            $hasError={!!displayError}
            onChange={e => updatePart('month', e.target.value)}
            aria-label={t('register.dateMonth')}
            aria-describedby={describedBy}
            aria-invalid={!!displayError || undefined}
            autoComplete="bday-month"
          >
            <option value="">{t('register.dateSelectMonth')}</option>
            {monthOptions.map(month => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </Select>
        </PartGroup>

        <PartGroup $minWidth="6.5rem" $flex="0 1 6.5rem">
          <SubLabel htmlFor={yearId}>{t('register.dateYear')}</SubLabel>
          <Select
            id={yearId}
            name={`${baseId}-year`}
            value={yearValue}
            disabled={disabled}
            $hasError={!!displayError}
            onChange={e => updatePart('year', e.target.value)}
            aria-label={t('register.dateYear')}
            aria-describedby={describedBy}
            aria-invalid={!!displayError || undefined}
            autoComplete="bday-year"
          >
            <option value="">{t('register.dateSelectYear')}</option>
            {yearOptions.map(year => (
              <option key={year} value={String(year)}>
                {year}
              </option>
            ))}
          </Select>
        </PartGroup>
      </DateRow>
      {displayError && (
        <ErrorText id={errorId} role="alert">
          {displayError}
        </ErrorText>
      )}
    </Wrapper>
  );
};

export default DateOfBirthField;
